import isNil from "lodash/isNil";
import { BookingDetailActivity } from "PFApp/booking/components/booking_detail_activity";
import { BookingDetailItem } from "PFApp/booking/components/booking_detail_item";
import { BookingPlaceholderAlert } from "PFApp/booking/components/booking_placeholder/booking_placeholder_alert";
import { usePlaceholderBooking } from "PFApp/booking/components/booking_placeholder/hooks/use_placeholder_booking";
import { DetailsPanelSource } from "PFApp/booking/components/details_panel";
import { BookingDetailItemWorkforceMember } from "PFApp/booking/parts/overview/detail_view/booking_detail/booking_detail_content/booking_detail_item_workforce_member";
import { useBookingActivityContext } from "PFApp/booking/parts/providers/booking_activity_context_provider";
import { Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Booking } from "PFTypes/booking";
import { useTranslation } from "react-i18next";

import css from "../../detail_view.module.scss";
import { BookingDetailSidePanelProfile } from "../booking_detail_side_panel_context/booking_detail_side_panel_context.types";
import { BookingSiblings } from "./booking_siblings";

type BookingDetailContentProps = {
  booking?: Booking;
  loading: boolean;
  profile: BookingDetailSidePanelProfile | undefined;
  goToDate?: (date: Date) => void;
  checkCanGoToDate?: (date: string) => boolean;
  source?: DetailsPanelSource;
};

export const BookingDetailContent = ({
  booking,
  profile,
  loading,
  goToDate,
  checkCanGoToDate,
  source
}: BookingDetailContentProps) => {
  const { activity, parentActivity, isActivityFetching, isParentActivityFetching, isParentActivityError } =
    useBookingActivityContext();
  const { checkBookingRequiresApproval } = usePlaceholderBooking();
  const bookingRequiresApproval = checkBookingRequiresApproval(booking);

  const { t } = useTranslation(["translation", "bookingModule"]);

  if (!booking || loading || (!!booking.activity_id && isActivityFetching)) {
    return <LoadingDots />;
  }

  if (isNil(profile)) {
    return <BookingDetailItem label={t("bookingModule:details.workforceMember")} value={<LoadingDots />} />;
  }

  return (
    <>
      <Stack className={css.root} gap="spacingLg">
        {bookingRequiresApproval && <BookingPlaceholderAlert booking={booking} />}
        <Stack>
          <BookingDetailItemWorkforceMember profile={profile} activity={activity} source={source} />
          {activity && (
            <>
              <BookingDetailActivity activity={activity} parentActivity={parentActivity} source={source} />
              {!isParentActivityError && (
                <BookingDetailActivity
                  activity={parentActivity}
                  isLoading={isParentActivityFetching}
                  source={source}
                />
              )}
            </>
          )}
        </Stack>
        <BookingSiblings goToDate={goToDate} checkCanGoToDate={checkCanGoToDate} />
      </Stack>
    </>
  );
};
